import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';

// Order status enum matching KDS workflow
export type OrderStatus =
  | 'pending'           // Just placed
  | 'confirmed'         // Restaurant confirmed
  | 'preparing'         // Being prepared in kitchen
  | 'ready'            // Ready for pickup/delivery
  | 'out_for_delivery' // Driver has picked up (delivery only)
  | 'delivered'        // Completed delivery
  | 'picked_up'        // Customer picked up (pickup only)
  | 'completed'        // Order complete
  | 'cancelled';       // Order cancelled

export type OrderType = 'pickup' | 'dine-in' | 'delivery';

export interface OrderItem {
  menuItemId: string;
  name: string;
  quantity: number;
  price: number;
  specialInstructions?: string;
}

export interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  zipCode?: string;
  deliveryInstructions?: string;
  tableNumber?: string;
  pickupTime?: string;
}

export interface DeliveryDriver {
  driverId: string;
  name: string;
  phone: string;
  vehicleInfo?: string;
  currentLocation?: {
    lat: number;
    lng: number;
  };
  estimatedArrival?: string;
}

export interface OnlineOrder {
  orderId: string;
  storeId: string;
  storeName: string;
  orderType: OrderType;
  status: OrderStatus;
  items: OrderItem[];
  customerInfo: CustomerInfo;
  subtotal: number;
  tax: number;
  tip: number;
  total: number;
  createdAt: string;
  updatedAt: string;
  estimatedReadyTime?: string;
  driver?: DeliveryDriver;
  statusHistory: Array<{
    status: OrderStatus;
    timestamp: string;
    note?: string;
  }>;
}

export interface FavoriteOrder {
  favoriteId: string;
  name: string;
  storeId: string;
  storeName: string;
  items: OrderItem[];
  orderType: OrderType;
  lastOrdered: string;
  orderCount: number;
}

interface OrderContextType {
  // Current order tracking
  currentOrder: OnlineOrder | null;
  setCurrentOrder: (order: OnlineOrder | null) => void;
  updateOrderStatus: (orderId: string, status: OrderStatus, note?: string) => void;

  // Order history
  orderHistory: OnlineOrder[];
  loadOrderHistory: () => Promise<void>;

  // Favorites
  favorites: FavoriteOrder[];
  addToFavorites: (order: OnlineOrder, name: string) => void;
  removeFromFavorites: (favoriteId: string) => void;
  reorderFromFavorite: (favoriteId: string) => OrderItem[];

  // WebSocket connection status
  isConnected: boolean;

  // Notifications
  enableNotifications: boolean;
  setEnableNotifications: (enabled: boolean) => void;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

const ORDER_HISTORY_KEY = 'bso_order_history';
const FAVORITES_KEY = 'bso_favorites';
const NOTIFICATIONS_KEY = 'bso_notifications_enabled';

export function useOrder() {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
}

export function OrderProvider({ children }: { children: React.ReactNode }) {
  const [currentOrder, setCurrentOrderState] = useState<OnlineOrder | null>(null);
  const [orderHistory, setOrderHistory] = useState<OnlineOrder[]>([]);
  const [favorites, setFavorites] = useState<FavoriteOrder[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [enableNotifications, setEnableNotificationsState] = useState(true);
  const [ws, setWs] = useState<WebSocket | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(ORDER_HISTORY_KEY);
      if (savedHistory) {
        setOrderHistory(JSON.parse(savedHistory));
      }

      const savedFavorites = localStorage.getItem(FAVORITES_KEY);
      if (savedFavorites) {
        setFavorites(JSON.parse(savedFavorites));
      }

      const savedNotifications = localStorage.getItem(NOTIFICATIONS_KEY);
      if (savedNotifications !== null) {
        setEnableNotificationsState(JSON.parse(savedNotifications));
      }
    } catch (error) {
      console.error('Error loading order data from localStorage:', error);
    }
  }, []);

  // Save order history to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(ORDER_HISTORY_KEY, JSON.stringify(orderHistory));
    } catch (error) {
      console.error('Error saving order history:', error);
    }
  }, [orderHistory]);

  // Save favorites to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  }, [favorites]);

  // Save notification preference
  useEffect(() => {
    try {
      localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(enableNotifications));
    } catch (error) {
      console.error('Error saving notification preference:', error);
    }
  }, [enableNotifications]);

  // WebSocket connection for real-time order updates
  useEffect(() => {
    if (!currentOrder) return;

    const wsUrl = API_ENDPOINTS.messaging?.websocketUrl;
    if (!wsUrl) {
      console.warn('WebSocket URL not configured, using polling fallback');
      // Fallback to polling
      const pollInterval = setInterval(() => {
        pollOrderStatus(currentOrder.orderId);
      }, 10000); // Poll every 10 seconds

      return () => clearInterval(pollInterval);
    }

    try {
      const websocket = new WebSocket(wsUrl);

      websocket.onopen = () => {
        console.log('Order tracking WebSocket connected');
        setIsConnected(true);
        // Subscribe to order updates
        websocket.send(JSON.stringify({
          action: 'subscribe',
          orderId: currentOrder.orderId,
          type: 'order_tracking'
        }));
      };

      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'order_update' && data.orderId === currentOrder.orderId) {
            handleOrderUpdate(data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      websocket.onclose = () => {
        console.log('Order tracking WebSocket disconnected');
        setIsConnected(false);
      };

      websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };

      setWs(websocket);

      return () => {
        websocket.close();
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [currentOrder?.orderId]);

  const pollOrderStatus = async (orderId: string) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.paymentLinks()}/order/${orderId}/status`);
      if (response.ok) {
        const data = await response.json();
        if (data.status && currentOrder && data.status !== currentOrder.status) {
          handleOrderUpdate({ status: data.status, timestamp: new Date().toISOString() });
        }
      }
    } catch (error) {
      console.error('Error polling order status:', error);
    }
  };

  const handleOrderUpdate = useCallback((data: any) => {
    setCurrentOrderState(prev => {
      if (!prev) return prev;

      const newStatus = data.status as OrderStatus;
      const timestamp = data.timestamp || new Date().toISOString();

      const updated: OnlineOrder = {
        ...prev,
        status: newStatus,
        updatedAt: timestamp,
        statusHistory: [
          ...prev.statusHistory,
          { status: newStatus, timestamp, note: data.note }
        ]
      };

      // Update driver info if provided
      if (data.driver) {
        updated.driver = data.driver;
      }

      // Update estimated time if provided
      if (data.estimatedReadyTime) {
        updated.estimatedReadyTime = data.estimatedReadyTime;
      }

      // Show notification if enabled
      if (enableNotifications && 'Notification' in window && Notification.permission === 'granted') {
        showStatusNotification(newStatus, prev.orderId);
      }

      return updated;
    });
  }, [enableNotifications]);

  const showStatusNotification = (status: OrderStatus, orderId: string) => {
    const messages: Record<OrderStatus, string> = {
      pending: 'Your order has been received',
      confirmed: 'Your order has been confirmed by the restaurant',
      preparing: 'Your order is being prepared',
      ready: 'Your order is ready!',
      out_for_delivery: 'Your order is on the way!',
      delivered: 'Your order has been delivered',
      picked_up: 'Thank you for picking up your order',
      completed: 'Order complete. Thank you!',
      cancelled: 'Your order has been cancelled'
    };

    new Notification('Order Update', {
      body: messages[status] || `Order status: ${status}`,
      icon: '/logo192.png',
      tag: orderId
    });
  };

  const setCurrentOrder = useCallback((order: OnlineOrder | null) => {
    setCurrentOrderState(order);

    // Add to history when order is set
    if (order) {
      setOrderHistory(prev => {
        const existing = prev.findIndex(o => o.orderId === order.orderId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = order;
          return updated;
        }
        return [order, ...prev].slice(0, 50); // Keep last 50 orders
      });
    }
  }, []);

  const updateOrderStatus = useCallback((orderId: string, status: OrderStatus, note?: string) => {
    const timestamp = new Date().toISOString();

    setCurrentOrderState(prev => {
      if (!prev || prev.orderId !== orderId) return prev;
      return {
        ...prev,
        status,
        updatedAt: timestamp,
        statusHistory: [
          ...prev.statusHistory,
          { status, timestamp, note }
        ]
      };
    });

    setOrderHistory(prev =>
      prev.map(order =>
        order.orderId === orderId
          ? {
              ...order,
              status,
              updatedAt: timestamp,
              statusHistory: [
                ...order.statusHistory,
                { status, timestamp, note }
              ]
            }
          : order
      )
    );
  }, []);

  const loadOrderHistory = useCallback(async () => {
    // In a full implementation, this would fetch from the API
    // For now, we rely on localStorage
    try {
      const savedHistory = localStorage.getItem(ORDER_HISTORY_KEY);
      if (savedHistory) {
        setOrderHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error('Error loading order history:', error);
    }
  }, []);

  const addToFavorites = useCallback((order: OnlineOrder, name: string) => {
    const favoriteId = `fav_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newFavorite: FavoriteOrder = {
      favoriteId,
      name,
      storeId: order.storeId,
      storeName: order.storeName,
      items: order.items,
      orderType: order.orderType,
      lastOrdered: order.createdAt,
      orderCount: 1
    };

    setFavorites(prev => [newFavorite, ...prev]);
  }, []);

  const removeFromFavorites = useCallback((favoriteId: string) => {
    setFavorites(prev => prev.filter(f => f.favoriteId !== favoriteId));
  }, []);

  const reorderFromFavorite = useCallback((favoriteId: string): OrderItem[] => {
    const favorite = favorites.find(f => f.favoriteId === favoriteId);
    if (!favorite) return [];

    // Update last ordered and count
    setFavorites(prev =>
      prev.map(f =>
        f.favoriteId === favoriteId
          ? { ...f, lastOrdered: new Date().toISOString(), orderCount: f.orderCount + 1 }
          : f
      )
    );

    return favorite.items;
  }, [favorites]);

  const setEnableNotifications = useCallback((enabled: boolean) => {
    setEnableNotificationsState(enabled);

    // Request notification permission if enabling
    if (enabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const value: OrderContextType = {
    currentOrder,
    setCurrentOrder,
    updateOrderStatus,
    orderHistory,
    loadOrderHistory,
    favorites,
    addToFavorites,
    removeFromFavorites,
    reorderFromFavorite,
    isConnected,
    enableNotifications,
    setEnableNotifications
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
}

export default OrderContext;
